// Owner: shell-nav (wave 2). The app's query client and the foundation queries (member, nav, model,
// members). The query cache reports every failed request to the connectivity watcher of the shell, so
// an unreachable API shows one banner instead of endless spinners (plan §9.2).
import {
  MutationCache,
  QueryCache,
  QueryClient,
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { isApiError } from '@dataroom/api-client';
import { recentItemsContract } from '@dataroom/contracts/nav';
import { ModelIndex } from '@dataroom/core';
import { api } from './api';

type RequestListener = (outcome: { ok: boolean; error?: unknown }) => void;
const requestListeners = new Set<RequestListener>();

/** Subscribe to the outcome of every query and mutation (the shell's connectivity watcher). */
export function onRequestOutcome(listener: RequestListener): () => void {
  requestListeners.add(listener);
  return () => requestListeners.delete(listener);
}
const report = (ok: boolean, error?: unknown) => {
  for (const l of [...requestListeners]) l(ok ? { ok } : { ok, error });
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => report(false, error),
    onSuccess: () => report(true),
  }),
  mutationCache: new MutationCache({
    onError: (error) => report(false, error),
    onSuccess: () => report(true),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(isApiError(err) && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

/**
 * Query keys of the foundation. Slices use their own prefixes (['workflows', …]) and invalidate
 * ['records', objectKey] after writes to records.
 */
export const qk = {
  me: ['me'] as const,
  nav: ['nav'] as const,
  recent: ['nav', 'recent'] as const,
  model: ['model'] as const,
  members: ['directory', 'members'] as const,
  objects: ['objects'] as const,
  object: (key: string) => ['objects', key] as const,
  records: (objectKey: string) => ['records', objectKey] as const,
  record: (objectKey: string, id: string) => ['records', objectKey, id] as const,
};

export const meQuery = queryOptions({ queryKey: qk.me, queryFn: () => api.auth.me(), staleTime: 5 * 60_000 });
export const navQuery = queryOptions({ queryKey: qk.nav, queryFn: () => api.nav.get(), staleTime: 30_000 });
/** The last items the member opened (rail Recent, empty ⌘K). */
export const recentQuery = (limit = 5) =>
  queryOptions({
    queryKey: [...qk.recent, limit],
    queryFn: ({ signal }) => api.call(recentItemsContract, { query: { limit } }, { signal }),
    staleTime: 15_000,
  });
export const modelQuery = queryOptions({
  queryKey: qk.model,
  queryFn: async () => new ModelIndex(await api.metadata.model()),
  staleTime: 60_000,
});
export const membersQuery = queryOptions({
  queryKey: qk.members,
  queryFn: () => api.directory.members('all'),
  staleTime: 5 * 60_000,
});
export const objectQuery = (key: string) =>
  queryOptions({ queryKey: qk.object(key), queryFn: () => api.metadata.objects.get(key) });

/** The signed-in member (routes under the authenticated layouts guarantee it is loaded). */
export function useMe() {
  return useSuspenseQuery(meQuery).data;
}
export function useNav() {
  return useQuery(navQuery);
}
export function useRecent(limit = 5) {
  return useQuery(recentQuery(limit));
}
/** Workspace model as an indexed ModelIndex. */
export function useModel() {
  return useQuery(modelQuery);
}
export function useMembers() {
  return useQuery(membersQuery);
}
/** Member lookup by id (avatars, owner cells). */
export function useMemberMap() {
  const { data } = useMembers();
  return new Map((data ?? []).map((m) => [m.id, m]));
}
